import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CoeService } from "../../services/dataService";

/* ─── Icons ─── */
const ArrowLeft = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
);
const FileDoc = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" fill="currentColor" viewBox="0 0 24 24">
    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
  </svg>
);
const FolderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 24 24">
    <path d="M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
  </svg>
);
const ChevronDown = ({ rotated, size = 18 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"
    style={{ transition: "transform 0.3s ease", transform: rotated ? "rotate(180deg)" : "rotate(0deg)", display: "block" }}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);
const ChevronRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M9 18l6-6-6-6" />
  </svg>
);
const Maximize = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
  </svg>
);
const Minimize = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 0 2 2v3" />
  </svg>
);
const X = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);
const EyeIcon = ({ size = 15 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const AlertCircle = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);
const BadgeCheck = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" />
  </svg>
);
const Layers = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" />
  </svg>
);
const SearchIcon = ({ size = 16, color = "currentColor" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);
const FilterIcon = ({ size = 16, color = "currentColor" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
  </svg>
);
const MoreDots = ({ size = 16, color = "currentColor" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="currentColor" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="2"></circle>
    <circle cx="12" cy="5" r="2"></circle>
    <circle cx="12" cy="19" r="2"></circle>
  </svg>
);

/* ─── Accent palette for categories ─── */
const ACCENT = [
  { bg: "#f8faff", border: "#e0e7ff", text: "#3b82f6", pill: "#eff6ff" },
  { bg: "#faf9ff", border: "#e9e4ff", text: "#8b5cf6", pill: "#f5f3ff" },
  { bg: "#f7fffb", border: "#dcfce7", text: "#10b981", pill: "#ecfdf5" },
  { bg: "#fffbf7", border: "#ffedd5", text: "#f97316", pill: "#fff7ed" },
];

/* ── Domain card palette (Inspired by reference image) ── */
const PALETTE = [
  {
    grad: "linear-gradient(135deg, #93c5fd 0%, #3b82f6 100%)",
    accent: "#ffffff",
    shadow: "rgba(59, 130, 246, 0.25)",
    rings: "rgba(255, 255, 255, 0.2)"
  }
];

const VIEW_DOMAINS = "domains";
const VIEW_CONTENT = "content";
const VIEW_PDF = "pdf";

const PDFViewer = () => {
  const navigate = useNavigate();
  const userRole = useSelector((s) => s.authorise?.userRole);
  const containerRef = useRef(null);

  const [view, setView] = useState(VIEW_DOMAINS);
  const [allDocs, setAllDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [expandedCoe, setExpandedCoe] = useState(null);
  const [expandedCat, setExpandedCat] = useState({});
  const [expandedSubDocs, setExpandedSubDocs] = useState({});
  const [activeHeaderDropdown, setActiveHeaderDropdown] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [boardGroupExpanded, setBoardGroupExpanded] = useState({});
  const [currentDoc, setCurrentDoc] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const isEducator = userRole === "educator";

  /* ── Fetch all COE documents ── */
  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const res = isEducator ? await CoeService.getEducatorDocuments() : await CoeService.getSpocDocuments();
        setAllDocs(res.data || []);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load COE documents.");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [userRole, isEducator]);

  /* ── Group by domain ── */
  const domainMap = {};
  allDocs.forEach((doc) => {
    // Each domain is independent — group by domain_id directly
    const key = `${doc.academy_id}__${doc.domain_id}`;

    if (!domainMap[key]) {
      domainMap[key] = {
        key,
        academy_id: doc.academy_id,
        domain_id: doc.domain_id,
        domain_name: (doc.domain_name || "Unknown Domain").replace(/\s*#\d+$/, "").trim(),
        academy_name: doc.academy_name || "Unknown Corporate",
        docs: [],
        hasRootDoc: false
      };
    }

    // Backend enforces all access control — skip any legacy placeholders
    if (doc.doc_type !== "coe_content" && doc.is_active !== false) {
      domainMap[key].docs.push(doc);
      if (doc.doc_type === "coe_panel") {
        domainMap[key].hasRootDoc = true;
      }
    }
  });

  // Show all domains with real content — backend already enforced access
  const domains = Object.values(domainMap).filter(d => d.hasRootDoc);

  /* ── Build hierarchy: COE panels → nodes (recursive tree) ── */
  const buildHierarchy = (docs) => {
    const panels = {};
    const docById = {};

    docs.forEach(d => { docById[d.coe_id] = d; });

    // Build primary COE panels — only proper coe_panel type docs
    docs.filter(d => d.doc_type === "coe_panel").forEach(d => {
      panels[d.coe_id] = {
        title: d.doc_title || "COE Module",
        coe_id: d.coe_id,
        children: []
      };
    });

    const childrenMap = {};
    docs.forEach(d => {
      if (d.parent_id && d.doc_type !== "coe_panel" && d.doc_type !== "coe_content") {
        if (!childrenMap[d.parent_id]) childrenMap[d.parent_id] = [];
        childrenMap[d.parent_id].push(d);
      }
    });

    const buildTree = (doc) => {
      const node = { ...doc };
      const children = childrenMap[doc.coe_id] || [];
      node.subDocs = children
        .map(child => buildTree(child))
        .sort((a, b) => {
          if (a.is_folder && !b.is_folder) return -1;
          if (!a.is_folder && b.is_folder) return 1;
          return (a.created_at ? new Date(a.created_at).getTime() : 0) - (b.created_at ? new Date(b.created_at).getTime() : 0);
        });
      return node;
    };

    Object.values(panels).forEach(panel => {
      panel.children = (childrenMap[panel.coe_id] || [])
        .map(child => buildTree(child))
        .sort((a, b) => {
          if (a.is_folder && !b.is_folder) return -1;
          if (!a.is_folder && b.is_folder) return 1;
          return (a.created_at ? new Date(a.created_at).getTime() : 0) - (b.created_at ? new Date(b.created_at).getTime() : 0);
        });
    });

    return Object.values(panels)
      .sort((a, b) => {
        const docA = docById[a.coe_id];
        const docB = docById[b.coe_id];
        const timeA = docA?.created_at ? new Date(docA.created_at).getTime() : 0;
        const timeB = docB?.created_at ? new Date(docB.created_at).getTime() : 0;
        return timeA - timeB;
      });
  };

  const hierarchy = selectedDomain ? buildHierarchy(domainMap[selectedDomain.key]?.docs || []) : [];

  /* ── Fullscreen ── */
  const handleFullscreen = async () => {
    if (!isFullscreen) {
      try { await (containerRef.current?.requestFullscreen?.() || containerRef.current?.webkitRequestFullscreen?.()); setIsFullscreen(true); } catch { }
    } else {
      try { await (document.exitFullscreen?.() || document.webkitExitFullscreen?.()); setIsFullscreen(false); } catch { }
    }
  };
  useEffect(() => {
    const sync = () => setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => { document.removeEventListener("fullscreenchange", sync); document.removeEventListener("webkitfullscreenchange", sync); };
  }, []);

  const toEmbedUrl = (link) => {
    if (!link) return "";
    const m = link.match(/\/d\/([a-zA-Z0-9_-]+)/);
    return m ? `https://drive.google.com/file/d/${m[1]}/preview` : link;
  };

  const toggleCoe = (id) => setExpandedCoe(prev => prev === id ? null : id);

  /* ──────────────── LOADING ──────────────── */
  if (isLoading) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, gap: 10 }}>
      <div style={{ width: 44, height: 44, border: "4px solid #e2e8f0", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "coe-spin 0.8s linear infinite" }} />
      <p style={{ color: "#94a3b8", fontWeight: 700, margin: 0 }}>Loading COE Documents…</p>
      <style>{`@keyframes coe-spin { to { transform:rotate(360deg); } } @keyframes coe-pulse { 0%,100%{opacity:1} 50%{opacity:.4} }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ maxWidth: 520, margin: "40px auto", padding: 40, background: "#fff", borderRadius: 20, border: "1px solid #e2e8f0", textAlign: "center", boxShadow: "0 10px 40px rgba(0,0,0,0.06)" }}>
      <div style={{ width: 64, height: 64, background: "#eff6ff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}><AlertCircle /></div>
      <h3 style={{ fontSize: 18, fontWeight: 800, color: "#1e293b", margin: "0 0 8px" }}>Notice</h3>
      <p style={{ color: "#64748b", lineHeight: 1.7, fontWeight: 500, margin: 0 }}>{error}</p>
      {isEducator && (
        <button onClick={() => navigate("/educator-event")} style={{ marginTop: 24, padding: "10px 24px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 12, fontWeight: 700, fontSize: 12, cursor: "pointer", letterSpacing: 1, textTransform: "uppercase" }}>
          Go to FDP Certificates
        </button>
      )}
    </div>
  );

  /* ──────────────── PDF VIEWER ──────────────── */
  if (view === VIEW_PDF && currentDoc) {
    return (
      <div ref={containerRef} style={{ width: "100%", fontFamily: "sans-serif", paddingTop: 24, background: isFullscreen ? "#fff" : "transparent" }}>
        {/* Header bar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", background: "#f8fafc", borderRadius: "16px 16px 0 0", border: "1px solid #e2e8f0", borderBottom: "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => { setView(VIEW_CONTENT); setCurrentDoc(null); }} style={{ display: "flex", alignItems: "center", gap: 8, border: "none", background: "transparent", cursor: "pointer", color: "#3b82f6", fontWeight: 700, fontSize: 14, padding: 0 }}>
              <ArrowLeft /> Back
            </button>
            <div style={{ width: 1, height: 20, background: "#e2e8f0", margin: "0 4px" }} />
            <div>
              <p style={{ fontSize: 10, fontWeight: 800, color: "#7c3aed", textTransform: "uppercase", letterSpacing: 2, margin: 0 }}>{currentDoc.academy_name}</p>
              <p style={{ fontSize: 14, fontWeight: 800, color: "#1e293b", margin: "4px 0 0", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{currentDoc.doc_title || currentDoc.domain_name}</p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleFullscreen} title="Fullscreen" style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", background: "#e2e8f0", color: "#64748b", border: "none", borderRadius: 8, cursor: "pointer" }}>
              {isFullscreen ? <Minimize /> : <Maximize />}
            </button>
            <button onClick={() => { setView(VIEW_CONTENT); setCurrentDoc(null); if (isFullscreen) document.exitFullscreen?.(); }} style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", background: "#fee2e2", color: "#ef4444", border: "none", borderRadius: 8, cursor: "pointer" }}>
              <X />
            </button>
          </div>
        </div>
        {/* iFrame */}
        <div style={{ border: "1px solid #e2e8f0", borderRadius: "0 0 16px 16px", overflow: "hidden", position: "relative", boxShadow: "0 20px 40px rgba(0,0,0,0.08)", background: "#333" }}>
          <iframe
            key={currentDoc.coe_id}
            src={toEmbedUrl(currentDoc.doc_link)}
            style={{ width: "100%", border: "none", display: "block", height: isFullscreen ? "calc(100vh - 100px)" : 820 }}
            title={currentDoc.doc_title}
            allow="autoplay"
          />
          <div style={{ position: "absolute", top: 0, right: 0, width: 96, height: 56, background: "#333", zIndex: 20 }} />
          <div style={{ position: "absolute", bottom: 0, right: 0, width: 70, height: 10, background: "#333", zIndex: 20 }} />
        </div>
      </div>
    );
  }

  /* ──────────────── CONTENT VIEW (Recursive Tree) ──────────────── */
  if (view === VIEW_CONTENT && selectedDomain) {
    return (
      <div style={{ width: "100%", maxWidth: "950px", fontFamily: "'Inter','Segoe UI',sans-serif", padding: "20px 24px 40px" }}>

        {/* Breadcrumb */}


        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
          <button onClick={() => { setView(VIEW_DOMAINS); setSelectedDomain(null); setExpandedCoe(null); setExpandedSubDocs({}); }}
            style={{ display: "flex", alignItems: "center", gap: 6, border: "none", background: "#eff6ff", padding: "7px 14px", borderRadius: 10, cursor: "pointer", color: "#2563eb", fontWeight: 700, fontSize: 12 }}>
            <ArrowLeft /> Back
          </button>
          <span style={{ color: "#cbd5e1", fontSize: 12 }}>/</span>
          <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>Center of Excellence</span>
          <span style={{ color: "#cbd5e1", fontSize: 12 }}>/</span>
          <span style={{ fontSize: 12, color: "#334155", fontWeight: 700 }}>{selectedDomain.domain_name}</span>
        </div>

        {/* Domain banner */}
        <div style={{ background: "#60a5fa", borderRadius: 16, padding: "24px 32px", marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", overflow: "hidden" }}>
          {/* Decorative Rings */}
          <div style={{ position: "absolute", right: "-2%", top: "-30%", width: 140, height: 140, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.15)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", right: "4%", bottom: "-50%", width: 100, height: 100, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.1)", pointerEvents: "none" }} />

          <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(255, 255, 255, 0.9)", textTransform: "uppercase", letterSpacing: 1.5, border: "1px solid rgba(255,255,255,0.3)", padding: "4px 12px", borderRadius: 99 }}>{selectedDomain.academy_name}</span>
            <h2 style={{ fontSize: 28, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: -0.5 }}>{selectedDomain.domain_name}</h2>
          </div>
          <div style={{ display: "flex", gap: 16, position: "relative", zIndex: 1 }}>
            <div style={{ textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center", background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)", padding: "12px 24px", borderRadius: 12 }}>
              <div style={{ fontSize: 28, fontWeight: 600, color: "#fff", lineHeight: 1 }}>{hierarchy.length}</div>
              <div style={{ fontSize: 10, color: "#fff", textTransform: "uppercase", letterSpacing: 1, fontWeight: 600, marginTop: 4 }}>COE{hierarchy.length !== 1 ? 's' : ''}</div>
            </div>
          </div>
        </div>

        {/* Modules */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {hierarchy.map((coe, cIdx) => {
            const isExp = expandedCoe === cIdx;
            const children = coe.children || [];
            return (
              <div key={coe.coe_id} style={{ background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb", overflow: "hidden", marginBottom: 16 }}>
                <button onClick={() => toggleCoe(cIdx)}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 16, padding: "16px 20px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}>
                  <div style={{ width: 44, height: 44, borderRadius: 8, background: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexShrink: 0 }}>
                    <Layers />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 16, fontWeight: 500, color: "#111827", margin: 0 }}>{coe.title}</p>
                    <p style={{ fontSize: 13, color: "#4b5563", margin: "4px 0 0", fontWeight: 400 }}>{children.length} items</p>
                  </div>
                  <div style={{ padding: "6px 16px", border: "1px solid #d1d5db", borderRadius: 99, fontSize: 13, color: "#4b5563", fontWeight: 500, background: "#fff" }}>
                    {isExp ? "Expanded" : "Expand"}
                  </div>
                </button>

                <AnimatePresence>
                  {isExp && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>



                      {/* Grid Block Folder Layout */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16, padding: "16px 20px" }}>
                        {children.map((folder, colIdx) => {
                          const ac = ACCENT[colIdx % ACCENT.length];
                          const isTopExpanded = expandedSubDocs[folder.coe_id] === true;
                          return (
                            <div key={folder.coe_id} style={{ background: "#f8fafc", borderRadius: 16, padding: "16px", border: "1px solid #f1f5f9", height: "fit-content" }}>
                              {/* Block Header */}
                              <div
                                onClick={() => setExpandedSubDocs(p => ({ ...p, [folder.coe_id]: !isTopExpanded }))}
                                style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: isTopExpanded ? 16 : 0, cursor: "pointer" }}>
                                <div style={{ width: 40, height: 40, borderRadius: 10, background: ac.pill, display: "flex", alignItems: "center", justifyContent: "center", color: ac.text, flexShrink: 0 }}>
                                  <FolderIcon />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <p style={{ fontSize: 14, fontWeight: 600, color: "#1f2937", margin: 0 }}>{folder.doc_title}</p>
                                  <p style={{ fontSize: 12, color: "#6b7280", margin: "2px 0 0" }}>{folder.subDocs?.length || 0} items</p>
                                </div>
                                <div style={{ color: "#d1d5db", transform: isTopExpanded ? "rotate(0deg)" : "rotate(180deg)", transition: "transform 0.3s" }}>
                                  <ChevronDown rotated={false} size={16} />
                                </div>
                              </div>

                              {/* Block Items */}
                              <AnimatePresence>
                                {isTopExpanded && (
                                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                      {(() => {
                                        const renderColumnItem = (item, accent, depth = 0) => {
                                          const isFolder = item.is_folder;
                                          const hasSub = item.subDocs && item.subDocs.length > 0;
                                          const isExpanded = expandedSubDocs[item.coe_id];

                                          return (
                                            <div key={item.coe_id}>
                                              <div
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (hasSub) {
                                                    setExpandedSubDocs(p => ({ ...p, [item.coe_id]: !p[item.coe_id] }));
                                                  } else if (!isFolder && item.doc_link) {
                                                    setCurrentDoc(item); setView(VIEW_PDF);
                                                  }
                                                }}
                                                style={{
                                                  display: "flex", alignItems: "center", gap: 12, padding: "12px",
                                                  background: depth > 0 ? "#f9fafb" : "#fff",
                                                  borderRadius: 12,
                                                  cursor: (hasSub || (!isFolder && item.doc_link)) ? "pointer" : "default",
                                                  transition: "all 0.2s",
                                                  border: "1px solid",
                                                  borderColor: isExpanded && hasSub ? "#e5e7eb" : "transparent",
                                                  marginLeft: depth > 0 ? 12 : 0,
                                                  marginTop: depth > 0 ? 8 : 0
                                                }}
                                                onMouseEnter={e => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.background = "#fff"; e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.02)"; }}
                                                onMouseLeave={e => { if (!(isExpanded && hasSub)) { e.currentTarget.style.borderColor = "transparent"; e.currentTarget.style.background = depth > 0 ? "#f9fafb" : "#fff"; } e.currentTarget.style.boxShadow = "none"; }}
                                              >
                                                <div style={{ width: 40, height: 40, borderRadius: 10, background: accent.pill, display: "flex", alignItems: "center", justifyContent: "center", color: accent.text, flexShrink: 0 }}>
                                                  {isFolder ? <FolderIcon /> : <FileDoc />}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                  <p style={{ fontSize: 13, fontWeight: 500, color: "#374151", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.doc_title}</p>
                                                  <p style={{ fontSize: 11, color: "#6b7280", margin: "2px 0 0" }}>{isFolder ? `Folder · ${item.subDocs?.length || 0} items` : (hasSub ? `Document · ${item.subDocs.length} versions` : "Document")}</p>
                                                </div>

                                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                  {!isFolder && item.doc_link && hasSub && (
                                                    <div
                                                      onClick={(e) => { e.stopPropagation(); setCurrentDoc(item); setView(VIEW_PDF); }}
                                                      title="View Document"
                                                      style={{ padding: "6px", borderRadius: "8px", color: "#6b7280", background: "#f3f4f6", cursor: "pointer", transition: "all 0.2s" }}
                                                      onMouseEnter={e => { e.currentTarget.style.color = "#2563eb"; e.currentTarget.style.background = "#eff6ff"; }}
                                                      onMouseLeave={e => { e.currentTarget.style.color = "#6b7280"; e.currentTarget.style.background = "#f3f4f6"; }}
                                                    >
                                                      <EyeIcon size={14} />
                                                    </div>
                                                  )}
                                                  {hasSub && (
                                                    <div style={{ color: "#d1d5db", transform: isExpanded ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}>
                                                      <ChevronRight />
                                                    </div>
                                                  )}
                                                </div>
                                              </div>

                                              <AnimatePresence>
                                                {isExpanded && hasSub && (
                                                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
                                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                                      {item.subDocs.map(sub => renderColumnItem(sub, accent, depth + 1))}
                                                    </div>
                                                  </motion.div>
                                                )}
                                              </AnimatePresence>
                                            </div>
                                          );
                                        };
                                        return folder.subDocs?.map(item => renderColumnItem(item, ac));
                                      })()}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                      </div>

                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  /* ──────────────── DOMAIN GRID ──────────────── */
  return (
    <div style={{ width: "100%", maxWidth: "1600px", margin: "0 auto", fontFamily: "'Inter', 'Segoe UI', sans-serif", padding: "28px 28px 48px" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        @keyframes coe-spin { to { transform: rotate(360deg); } }
        .coe-card:hover { transform: translateY(-5px) !important; }
      `}</style>

      {/* Header & Dashboard Stats */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32 }}>

        <div>
          <h1 style={{ fontSize: 30, fontWeight: 800, color: "#1e3a8a", margin: 0, letterSpacing: -0.8 }}>Center of Excellence</h1>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "8px 16px", gap: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
            <div>
              <p style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, margin: 0 }}>Domains</p>
              <p style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", margin: "2px 0 0", lineHeight: 1 }}>{domains.length}</p>
            </div>
          </div>


        </div>
      </div>

      {/* Cards */}
      {!isLoading && domains.length === 0 ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 20px", background: "#f8fafc", borderRadius: 16, border: "1px dashed #cbd5e1" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
            <FolderIcon size={28} color="#94a3b8" />
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: "#334155", margin: "0 0 8px" }}>No Documents Found</h3>
          <p style={{ fontSize: 13, color: "#94a3b8", margin: 0, textAlign: "center", maxWidth: 360 }}>
            There are no COE documents available for your institute at this time.
          </p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 24 }}>
          {domains.map((domain, idx) => {
            const pal = PALETTE[idx % PALETTE.length];
            const cnt = domain.docs.filter(d => d.doc_type !== "coe_panel" && d.doc_type !== "coe_content" && !d.is_folder).length;
            return (
              <button key={idx} className="coe-card"
                onClick={() => { setSelectedDomain(domain); setView(VIEW_CONTENT); setExpandedCoe(null); setExpandedSubDocs({}); }}
                style={{ display: "flex", flexDirection: "column", background: pal.grad, border: "none", borderRadius: 14, padding: "20px", cursor: "pointer", textAlign: "left", transition: "transform 0.3s, box-shadow 0.3s", position: "relative", overflow: "hidden", boxShadow: `0 8px 20px ${pal.shadow}`, color: "#fff", minHeight: 190 }}>
                <div style={{ position: "absolute", bottom: -20, right: -20, width: 110, height: 110, borderRadius: "50%", border: `1px solid ${pal.rings}`, opacity: 0.5 }} />
                <div style={{ position: "absolute", bottom: -40, right: -40, width: 160, height: 160, borderRadius: "50%", border: `1px solid ${pal.rings}`, opacity: 0.25 }} />

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, position: "relative", zIndex: 1 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, background: "rgba(255,255,255,0.22)", padding: "4px 10px", borderRadius: 20, textTransform: "uppercase", letterSpacing: 0.8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "calc(100% - 32px)" }} title={domain.academy_name}>{domain.academy_name}</span>
                  <div style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><FolderIcon size={16} /></div>
                </div>

                <div style={{ flex: 1 }} />

                <div style={{ position: "relative", zIndex: 1, marginBottom: 12 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 800, color: "#fff", margin: 0, lineHeight: 1.25, letterSpacing: "-0.2px" }}>{domain.domain_name}</h3>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", zIndex: 1 }}>
                  <span style={{ fontSize: 12, fontWeight: 800 }}>{cnt} <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.85 }}>DOCS</span></span>
                  <div style={{ width: 22, height: 22, borderRadius: 6, background: "rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "center" }}><ChevronRight size={14} /></div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PDFViewer;
